import React from 'react';
import ValidationElement from '../ValidationElement';
import { stateFormErrorConnector } from '../shared/stateFormErrors';
import './InputDropdown.scss';

export class InputDropdown extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      // uid: `${props.name}-${super.guid()}`,
      hasErrors: false,
      isValid: false,
      heldValue: props.value,
      showSuggestions: false,
      highlightedSuggestion: 0,
      filteredSuggestions: [],
    };

    this.onChange = this.handleChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.updateHeldValue = this.updateHeldValue.bind(this);
    this.updateSelection = this.updateSelection.bind(this);
    this.updateSuggestions = this.updateSuggestions.bind(this);

    this.dropdown = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (JSON.stringify(prevProps.suggestions) !== JSON.stringify(this.props.suggestions)) {
      this.updateSuggestions(this.props.shouldRenderSuggestions(this.state.heldValue));
    }
    if (this.state.heldValue !== prevState.heldValue) {
      this.updateSuggestions(this.props.shouldRenderSuggestions(this.state.heldValue));
    }

    if (this.state.showSuggestions && !prevState.showSuggestions) {
      this.updateHeldValue(this.props.inputProps.value);
    }

    if (prevState.highlightedSuggestion !== this.state.highlightedSuggestion) {
      this.dropdown.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }

  handleChange(event) {
    this.props.inputProps.onChange(event, { newValue: event.currentTarget.value });
    this.props.onSuggestionsFetchRequested({ value: event.currentTarget.value });
    const renderSuggestions = this.props.shouldRenderSuggestions(event.currentTarget.value);
    this.setState({
      heldValue: event.currentTarget.value,
      highlightedSuggestion: 0,
      filteredSuggestions: this.props.suggestions,
      showSuggestions: renderSuggestions,
    });
  }

  updateHeldValue(value) {
    this.setState({
      heldValue: value,
    });
  }

  handleKeyDown(event) {
    // Handle key navigation
    const { keyCode } = event;

    this.props.inputProps.onKeyDown(event);

    if (this.state.showSuggestions && this.state.filteredSuggestions.length > 0) {
      switch (keyCode) {
        case 40: // ArrowDown
          if (this.state.filteredSuggestions.length > this.state.highlightedSuggestion + 1) {
            this.props.inputProps.onChange(event, { newValue: this.state.filteredSuggestions[this.state.highlightedSuggestion + 1]?.value });
            const highlightedSuggestion = this.state.highlightedSuggestion + 1; // eslint-disable-line react/no-access-state-in-setstate
            this.setState({
              highlightedSuggestion,
            });
          }
          break;
        case 38: // ArrowUp
          if (this.state.highlightedSuggestion > 0) {
            this.props.inputProps.onChange(event, { newValue: this.state.filteredSuggestions[this.state.highlightedSuggestion - 1]?.value });
            const highlightedSuggestion = this.state.highlightedSuggestion - 1; // eslint-disable-line react/no-access-state-in-setstate
            this.setState({
              highlightedSuggestion,
            });
          }
          break;
        case 13: // EnterKey
          this.updateSelection(event, this.state.filteredSuggestions[this.state.highlightedSuggestion]?.value, this.state.filteredSuggestions[this.state.highlightedSuggestion]?.text);
          this.setState({
            highlightedSuggestion: 0,
          });
          this.updateSuggestions(false);
          break;
        case 27: // EscapeKey
          if (this.state.showSuggestions) {
            this.updateSuggestions(false);
            this.props.inputProps.onChange(event, { newValue: this.state.heldValue });
          }
          break;
        default:
          break;
      }
    } else {
      this.handleChange(event);
    }
  }

  onMouseEnter(event) {
    if (this.state.filteredSuggestions.length > 0) {
      this.setState({
        highlightedSuggestion: Number(event.target.attributes.dataindex.value),
      });
    }
  }

  onClick(event) {
    // For clicking a suggestion

    this.updateSelection(event, event.target.attributes.datavalue.value, event.target.attributes.datatext.value);
    this.updateSuggestions(false);
  }

  updateSelection(event, selection, selectedText) {
    this.props.onSuggestionSelected(event, { suggestion: { value: selection, text: selectedText } });
  }

  updateSuggestions(shown) {
    const renderSuggestions = shown && this.props.shouldRenderSuggestions(this.state.heldValue);
    this.setState({
      highlightedSuggestion: 0,
      filteredSuggestions: this.props.suggestions,
      showSuggestions: renderSuggestions,
    });
  }

  handleMouseDown(event) {
    event.preventDefault();
  }

  handleFocus(event) {
    super.handleFocus(event);
  }

  handleBlur(event) {
    this.updateSuggestions(false);
    super.handleBlur(event);
  }

  render() {
    let suggestionsList;
    const highlightIndex = this.state.highlightedSuggestion;
    const style = this.props.hasFullLengthOptions ? { width: '100%' } : {};

    if (this.state.showSuggestions) {
      if (this.state.filteredSuggestions.length > 0) {
        suggestionsList = (
          <ul role="listbox" className="input-dropdown-suggestions" style={style}>
            {this.state.filteredSuggestions.map((suggestion, index) => {
              let className;
              let scrollRef;
              const displayText = (suggestion.value === suggestion.text || this.props.suggestionsRenderProvided) ? `${suggestion.text}` : `${suggestion.value} (${suggestion.text})`;

              if (index === highlightIndex) {
                className = 'suggestion-highlight';
                scrollRef = this.dropdown;
              }
              // no need for a keypress event since the input field handles the keyboard actions changeiong the sections and choosing the items
              return (
                <li // eslint-disable-line
                  className={className}
                  key={`${suggestion.value}-${index}`}
                  onClick={this.onClick} // eslint-disable-line @typescript-eslint/unbound-method
                  ref={scrollRef}
                  onMouseDown={this.handleMouseDown}// eslint-disable-line @typescript-eslint/unbound-method
                  onMouseEnter={this.onMouseEnter}// eslint-disable-line @typescript-eslint/unbound-method
                  datavalue={suggestion.value}
                  datatext={suggestion.text}
                  dataindex={index}
                >
                  {displayText}
                </li>
              );
            })}
          </ul>
        );
      }
    }

    const containerClassName = this.state.showSuggestions
      ? 'input-dropdown-suggestions-container input-dropdown-suggestion-container-open' : 'input-dropdown-suggestions-container';

    return (
      <div role="combobox" aria-haspopup="listbox" aria-expanded={false} aria-controls={this.props.inputProps?.id} className={containerClassName}>
        <input
          id={this.props.inputProps?.id}
          className={this.props.inputProps?.className}
          type="text"
          autoComplete="off"
          aria-autocomplete="list"
          disabled={this.props.inputProps.disabled}
          placeholder={this.props.inputProps.placeholder}
          name={this.props.inputProps.name}
          onChange={this.handleChange}// eslint-disable-line @typescript-eslint/unbound-method
          onKeyDown={this.handleKeyDown}// eslint-disable-line @typescript-eslint/unbound-method
          onClick={this.handleChange}// eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}// eslint-disable-line @typescript-eslint/unbound-method
          pattern={this.props.inputProps.pattern}
          maxLength={this.props.inputProps.maxLength}
          readOnly={this.props.inputProps?.readOnly}
          value={this.props.inputProps.value}
          onCopy={this.props.inputProps?.onCopy}
          onPaste={this.props.inputProps?.onPaste}
          onCut={this.props.inputProps?.onCut}
          aria-label={this.props.inputProps?.ariaLabel || this.props.name}
        />
        {suggestionsList}
      </div>
    );
  }
}

InputDropdown.defaultProps = {
  id: '',
  name: 'inputdropdown',
  placeholder: '',
  disabled: false,
  maxLength: 255,
  readOnly: false,
  suggestions: [{ text: 'test1', value: 'test1' }, { text: 'test2', value: 'test2' }],
  suggestionsRenderProvided: false,
  onSuggestionSelected: (event, options) => { },
  onSuggestionsFetchRequested: (query) => { },
  onSuggestionsClearRequested: (value) => { },
  shouldRenderSuggestions: (value) => true,
  getSuggestionValue: (suggestion) => '',
  renderSuggestion: (suggestion, search) => { },
  inputProps: {},
  highlightFirstSuggestion: false,
  hasFullLengthOptions: false,
};

export default stateFormErrorConnector(InputDropdown);
